import { useEffect, useMemo, useState } from 'react';
import Divider from '@mui/material/Divider';
import Stack from '@mui/material/Stack';
import Button from '@mui/material/Button';
import ButtonLoading from '@mui/lab/LoadingButton';
import IconButton from '@mui/material/IconButton';
import Typography from '@mui/material/Typography';
import Modal from '@mui/material/Modal';
import HighlightOffRoundedIcon from '@mui/icons-material/HighlightOffRounded';
import DragIndicatorRoundedIcon from '@mui/icons-material/DragIndicatorRounded';
import CancelIcon from '@mui/icons-material/Cancel';
import TextField from '@mui/material/TextField';
import { RectangleCheckBoxComponent } from 'components/Form/RectangleCheckBoxComponent';
import { useGetAllMyViewItemList } from 'services/v1/Tenant/ConfigurationService';
import {
  MyViewDashboardDefinitionItem,
  MyViewItemConfig,
  MyViewSelectionItem,
} from 'types/api/Tenant/ConfigurationTypes';
import ActivityIndicator from 'components/ActivityIndicatorComponent';
import CloseRoundedIcon from '@mui/icons-material/CloseRounded';
import { matchSorter } from 'match-sorter';
import { Chip } from '@mui/material';
import { getOrderedMyViewSelection, normalizeMyViewItemOrder } from './MyViewSelectionUtils';

const ModalContentStyle = {
  position: 'absolute' as const,
  top: '50%',
  left: '50%',
  transform: 'translate(-50%, -50%)',
  width: '70vw',
  bgcolor: 'background.paper',
  maxHeight: '80vh',
  minHeight: '80vh',
  borderRadius: 1,
  py: 2,
};
const textInputStyles = {
  width: '100%',
  '& .MuiOutlinedInput': {
    padding: '1px 2px',
  },
};

const closeIconStyle = { color: '#98A2AE', cursor: 'pointer' };

export interface ModalSelectMyViewItemProps {
  visible?: boolean;
  onClose?: () => void;
  onSave?: (data: MyViewSelectionItem[]) => void;
  selectedItemIds: Set<string>;
  selectedDashboard: MyViewDashboardDefinitionItem | null;
  existingRowData?: any[];
}

export const getComponentTypeLabel = (componentType: string) => {
  switch (componentType) {
    case 'K_TREE':
      return 'K-Tree';
    case 'SUBJECT':
      return 'Subject';
    case 'SUBJECT_PROCEDURE':
      return 'Subject Procedure';
    case 'TABLE_ANALYTIC':
      return 'Table Analytic';
    case 'EVENT':
      return 'Event';
    case 'CUBE':
      return 'Cube';
    case 'INSIGHT_VIEW':
      return 'Insight View';
    case 'STANDARD_VIEW':
      return 'Standard View';
    case 'DASHBOARD':
      return 'Dashboard';
    case 'CHART':
      return 'Chart';
    default:
      return '';
  }
};

function ReorderList(props: {
  items: MyViewItemConfig[];
  onReorder: (items: MyViewItemConfig[]) => void;
  onRemove: (itemId: string) => void;
}) {
  const { items, onReorder, onRemove } = props;
  const [draggedItem, setDraggedItem] = useState<string | null>(null);
  const [dragOverItem, setDragOverItem] = useState<string | null>(null);

  const handleDragStart = (e: React.DragEvent, itemId: string) => {
    setDraggedItem(itemId);
    e.dataTransfer.effectAllowed = 'move';
    e.dataTransfer.setData('text/html', itemId);
  };

  const handleDragOver = (e: React.DragEvent, itemId: string) => {
    e.preventDefault();
    e.dataTransfer.dropEffect = 'move';
    if (draggedItem !== itemId) {
      setDragOverItem(itemId);
    }
  };

  const handleDragLeave = () => {
    setDragOverItem(null);
  };

  const handleDrop = (e: React.DragEvent, targetItemId: string) => {
    e.preventDefault();
    if (!draggedItem || draggedItem === targetItemId) {
      setDraggedItem(null);
      setDragOverItem(null);
      return;
    }

    const draggedIndex = items.findIndex((item) => item.id === draggedItem);
    const targetIndex = items.findIndex((item) => item.id === targetItemId);

    if (draggedIndex === -1 || targetIndex === -1) {
      setDraggedItem(null);
      setDragOverItem(null);
      return;
    }

    const newItems = [...items];
    const [removed] = newItems.splice(draggedIndex, 1);
    newItems.splice(targetIndex, 0, removed);

    onReorder(newItems);
    setDraggedItem(null);
    setDragOverItem(null);
  };

  const handleDragEnd = () => {
    setDraggedItem(null);
    setDragOverItem(null);
  };

  return (
    <Stack>
      <Typography variant='input-label-gray' mb={1} sx={{ fontSize: 12 }}>
        Click and drag to reorder the columns
      </Typography>

      <Stack>
        {items.map((item, index) => (
          <Stack
            key={item.id}
            draggable
            onDragStart={(e) => handleDragStart(e, item.id)}
            onDragOver={(e) => handleDragOver(e, item.id)}
            onDragLeave={handleDragLeave}
            onDrop={(e) => handleDrop(e, item.id)}
            onDragEnd={handleDragEnd}
            direction='row'
            alignItems='center'
            justifyContent={'space-between'}
            gap={1}
            sx={{
              mb: 1,
              border: `1px solid ${dragOverItem === item.id ? '#3B4797' : '#E0E0E0'}`,
              borderRadius: 2,
              padding: 1,
              cursor: 'move',
              backgroundColor: draggedItem === item.id ? '#f5f5f5' : 'transparent',
              opacity: draggedItem === item.id ? 0.5 : 1,
              transform: draggedItem === item.id ? 'rotate(2deg)' : 'none',
              transition: 'all 0.2s ease',
              '&:hover': {
                borderColor: '#3B4797',
                backgroundColor: '#f8f9fa',
              },
            }}
          >
            <Stack direction='row' alignItems='center' gap={1} sx={{ flex: 1 }}>
              <DragIndicatorRoundedIcon sx={{ color: '#98A2AE' }} />
              <Typography
                variant='input-label'
                letterSpacing={0.3}
                textAlign='left'
                sx={{ fontSize: 12, color: '#737373' }}
              >
                {item.displayName}
                <Chip
                  size='small'
                  sx={{ ml: 0.4, fontSize: 9, color: '#737373', backgroundColor: '#F0F0F0', borderRadius: 1 }}
                  label={
                    <Typography variant='input-label' sx={{ fontSize: 9, color: '#737373' }}>
                      {getComponentTypeLabel(item.componentType)}
                    </Typography>
                  }
                />
              </Typography>
            </Stack>
            <IconButton size='small' onClick={() => onRemove(item.id)} sx={{ color: '#98A2AE' }}>
              <CloseRoundedIcon />
            </IconButton>
          </Stack>
        ))}
      </Stack>
    </Stack>
  );
}

export function ModalSelectMyViewItem(props: Readonly<ModalSelectMyViewItemProps>) {
  const { onClose, visible = false, selectedItemIds, onSave, selectedDashboard, existingRowData } = props;

  const [selectedItem, setSelectedItem] = useState<Set<string>>(new Set());
  const { isLoading, data: myViewItems } = useGetAllMyViewItemList();
  const [searchKeyword, setSearchKeyword] = useState('');
  const [selectedTypeFilter, setSelectedTypeFilter] = useState<string | null>(null);

  const availableTypeFilters = useMemo(() => {
    if (!myViewItems?.data) return [];
    return Array.from(new Set(myViewItems.data.map((item) => item.componentType)));
  }, [myViewItems?.data]);

  const items: MyViewItemConfig[] = useMemo(() => {
    let base = myViewItems?.data ?? [];
    if (selectedTypeFilter) {
      base = base.filter((item) => item.componentType === selectedTypeFilter);
    }
    if (base.length > 0) {
      return matchSorter(base, searchKeyword, {
        keys: ['displayName', 'componentType'],
        baseSort: () => 0,
      });
    }
    return [];
  }, [myViewItems?.data, searchKeyword, selectedTypeFilter]);

  const [orderedSelectedItems, setOrderedSelectedItems] = useState<MyViewItemConfig[]>([]);

  const selectedItems = useMemo(() => {
    const orderedItems = orderedSelectedItems.filter((item) => selectedItem.has(item.id));
    const orderedItemIds = new Set(orderedItems.map((item) => item.id));
    const newlySelectedItems =
      myViewItems?.data?.filter((item) => selectedItem.has(item.id) && !orderedItemIds.has(item.id)) || [];

    return [...orderedItems, ...newlySelectedItems];
  }, [selectedItem, myViewItems?.data, orderedSelectedItems]);

  const handleOnCloseModal = () => {
    setSelectedItem(selectedItemIds);
    setOrderedSelectedItems([]);
    onClose?.();
  };

  const handleOnCancel = () => {
    handleOnCloseModal();
  };
  const handleOnSave = () => {
    const data = normalizeMyViewItemOrder(selectedItems).map((item) => {
      if (item.subjectId) {
        const id = item.id.split('#')[0];
        return {
          id,
          componentName: item.displayName,
          componentType: item.componentType,
          subjectId: item.subjectId,
          ktreeDefinitionId: item.ktreeDefinitionId,
          ktreeName: item.ktreeName,
          cubeDefinitionId: item.cubeDefinitionId,
          colIndexOrder: item.colIndexOrder,
        };
      }
      return {
        id: item.id,
        componentType: item.componentType,
        componentName: item.displayName,
        ktreeDefinitionId: item.ktreeDefinitionId,
        ktreeName: item.ktreeName,
        cubeDefinitionId: item.cubeDefinitionId,
        colIndexOrder: item.colIndexOrder,
      };
    });

    //@ts-ignore
    onSave?.(data);
    handleOnCloseModal();
  };

  const handleOnClick = (item: MyViewItemConfig) => {
    const isSelected = selectedItem.has(item.id);
    setSelectedItem((prevState) => {
      const nextState = new Set(prevState);
      if (isSelected) {
        nextState.delete(item.id);
      } else {
        nextState.add(item.id);
      }

      return nextState;
    });
    setOrderedSelectedItems((prev) => {
      if (isSelected) return prev.filter((selected) => selected.id !== item.id);
      if (prev.some((selected) => selected.id === item.id)) return prev;
      return [...prev, item];
    });
  };

  const handleOnOrder = (items: MyViewItemConfig[]) => {
    // Update the ordered items state
    setOrderedSelectedItems(items);
  };

  const handleOnRemove = (itemId: string) => {
    // Remove the item from selected items
    setSelectedItem((prev) => {
      const newSet = new Set(prev);
      newSet.delete(itemId);
      return newSet;
    });

    // Also remove from ordered items if it exists there
    setOrderedSelectedItems((prev) => prev.filter((item) => item.id !== itemId));
  };

  useEffect(() => {
    if (!visible || !myViewItems?.data) return;

    setSelectedItem(new Set(selectedItemIds));
    setOrderedSelectedItems(getOrderedMyViewSelection(myViewItems.data, selectedItemIds, existingRowData));
  }, [visible, selectedItemIds, myViewItems?.data, existingRowData]);

  return (
    <div>
      <Modal open={visible} onClose={handleOnCloseModal}>
        <Stack direction='column' sx={ModalContentStyle} px={2} justifyContent='space-between' flex={1} height='100%'>
          <Stack>
            <Stack justifyContent='space-between' direction='row' alignItems='center'>
              <Stack width='70%'>
                <Typography variant='body1' component='h2' fontWeight='bold' sx={{ color: '#3B4797' }}>
                  Add/Remove pages
                </Typography>
                <Typography variant='input-label-gray' mb={1}>
                  Select the pages you want to assign to {selectedDashboard?.dashboardName}
                </Typography>
              </Stack>
              <IconButton onClick={handleOnCloseModal} size='small'>
                <HighlightOffRoundedIcon sx={closeIconStyle} />
              </IconButton>
            </Stack>
            <Divider sx={{ my: 1 }} />
          </Stack>

          <Stack
            height='100%'
            width='100%'
            direction='row'
            justifyContent='space-between'
            gap={4}
            sx={{
              height: '100%',
              overflowY: 'auto',
            }}
          >
            <Stack sx={{ height: '100%', width: '68%' }} spacing={1}>
              <Stack direction='row' flexWrap='wrap' gap={0.5}>
                <Chip
                  label='All'
                  size='small'
                  variant={selectedTypeFilter === null ? 'filled' : 'outlined'}
                  onClick={() => setSelectedTypeFilter(null)}
                  color={selectedTypeFilter === null ? 'primary' : 'default'}
                />
                {availableTypeFilters.map((type) => (
                  <Chip
                    key={type}
                    label={getComponentTypeLabel(type)}
                    size='small'
                    variant={selectedTypeFilter === type ? 'filled' : 'outlined'}
                    onClick={() => setSelectedTypeFilter(selectedTypeFilter === type ? null : type)}
                    color={selectedTypeFilter === type ? 'primary' : 'default'}
                  />
                ))}
              </Stack>
              <TextField
                value={searchKeyword}
                onChange={(e) => setSearchKeyword(e.target.value)}
                name='search'
                sx={textInputStyles}
                size='small'
                placeholder='Search name or component...'
                type='text'
                InputProps={{
                  endAdornment: (
                    <CancelIcon
                      sx={{
                        display: searchKeyword ? 'block' : 'none',
                        fontSize: 14,
                        color: '#98A2AE',
                        cursor: 'pointer',
                      }}
                      onClick={() => setSearchKeyword('')}
                    />
                  ),
                }}
              />
              <Stack sx={{ padding: 1, border: '1px solid rgb(221, 221, 221)', bgcolor: 'rgb(248, 250, 252)' }}>
                <Typography variant='body1' component='h2' fontWeight='bold' sx={{ color: '#000' }} mb={3}>
                  Pages
                </Typography>
                {isLoading ? (
                  <Stack height={450}>
                    <ActivityIndicator />
                  </Stack>
                ) : (
                  <Stack
                    direction='row'
                    gap={1}
                    sx={{
                      borderRadius: 1,
                      display: 'flex',
                      flexWrap: 'wrap',
                      flexDirection: 'row',
                      gap: 1,
                    }}
                  >
                    {items.map((item) => {
                      let textName = item.displayName;

                      if (item.componentType === 'CHART') {
                        textName = item.insightViewName
                          ? `${item.displayName} (${item.insightViewName} - ${item.cubeDefinitionName})`
                          : item.displayName;
                      }

                      if (item.componentType === 'INSIGHT_VIEW') {
                        textName = item.cubeDefinitionName
                          ? `${item.displayName} (${item.cubeDefinitionName})`
                          : item.displayName;
                      }

                      return (
                        <RectangleCheckBoxComponent
                          searchValue={searchKeyword}
                          checked={selectedItem.has(item.id)}
                          key={item.id}
                          secondLabel={`${getComponentTypeLabel(item.componentType)}`}
                          text={textName}
                          onClick={() => handleOnClick(item)}
                        />
                      );
                    })}
                  </Stack>
                )}
              </Stack>
            </Stack>
            <Stack width='30%'>
              <ReorderList items={selectedItems} onReorder={handleOnOrder} onRemove={handleOnRemove} />
            </Stack>
          </Stack>
          <Stack px={2}>
            <Divider sx={{ mb: 1, mt: 2 }} />
            <Stack direction='row' justifyContent='flex-end' spacing={2} alignItems='center' sx={{ py: 1 }}>
              <Button variant='main-table-panel-border' onClick={handleOnCancel}>
                Cancel
              </Button>
              <ButtonLoading variant='main-table-panel' onClick={() => handleOnSave()}>
                Assign{selectedItems.length > 0 ? ` (${selectedItems.length})` : ''}
              </ButtonLoading>
            </Stack>
          </Stack>
        </Stack>
      </Modal>
    </div>
  );
}
